import { Chart } from "@highcharts/react";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import "highcharts/es-modules/masters/modules/pattern-fill.src.js";
import { useTranslation } from "react-i18next";
import { token } from "../../../../styled-system/tokens";
import { useAppSelector } from "../../../redux/hooks";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";

export function GoalProgressBar({
  current,
  totalGoal,
  todayGoal,
}: {
  current: number;
  totalGoal: number;
  todayGoal: number;
}) {
  const { t } = useTranslation(["goals"]);
  const pacing = current / todayGoal;
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const extraTicks = (todayGoal <= current || todayGoal >= totalGoal ? [current] : [current, todayGoal]).filter(
    (tick) => tick <= totalGoal,
  );

  const options: Highcharts.Options = {
    chart: {
      type: "solidgauge",
      height: 300,
    },

    title: { text: "" },
    tooltip: { enabled: false },
    legend: {
      enabled: false,
    },

    pane: {
      center: ["50%", "50%"],
      size: "85%",
      startAngle: -140,
      endAngle: 140,
      background: [
        {
          backgroundColor: token("colors.white"),
          borderColor: token("colors.border.default"),
          borderWidth: 1,
          borderRadius: "50%",
          innerRadius: "75%",
          outerRadius: "100%",
          shape: "arc",
        },
      ],
    },
    // the value axis
    yAxis: [
      {
        min: 0,
        max: totalGoal,
        startOnTick: false,
        endOnTick: false,
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: 0,
        tickPositions: [totalGoal],

        labels: {
          distance: -25,
          y: 26,
          formatter: (val) =>
            formatCurrency(val.value, {
              marketPlace: marketPlace,
              compact: true,
            }),
          style: { fontSize: token("fontSizes.small") },
        },
      },
      {
        linkedTo: 0,
        startOnTick: false,
        endOnTick: false,
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: 0,
        tickPositions: extraTicks,
        labels: {
          distance: 20,
          formatter: (val) =>
            formatCurrency(val.value, {
              marketPlace: marketPlace,
              compact: true,
            }),
        },
      },
    ],

    plotOptions: {
      solidgauge: {
        dataLabels: {
          y: 5,
          borderWidth: 0,
        },
      },
    },
    series: [
      {
        type: "solidgauge",
        borderRadius: "50%",
        data: [
          {
            y: todayGoal,
            color: {
              pattern: {
                path: {
                  d: "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11",
                  strokeWidth: 3,
                  stroke: token("colors.trellisPalette.17"),
                  opacity: 1,
                },
                width: 10,
                height: 10,
                opacity: 0.3,
                backgroundColor: token("colors.trellisPalette.17"),
              },
            },
          },
          {
            y: current,
            color: token("colors.trellisPalette.12"),
          },
        ],
        innerRadius: "75%",
        radius: "100%",
        dataLabels: {
          enabled: true,
          formatter: () => `${t(($) => $.goals["Pacing:"])}<br/>${formatPercent(pacing)}`,
          verticalAlign: "top",
          y: -20,
          style: {
            fontSize: token("fontSizes.small"),
          },
        },
      },
    ],
  };
  return <Chart options={options} />;
}
